/**
 * API Delay Middleware for Testing Race Conditions
 *
 * Adds artificial delays BEFORE and AFTER API requests in development/staging to simulate:
 * - Slow network initiation (before delay)
 * - Slow server response times (after delay)
 * - Race conditions between concurrent updates
 * - User continuing to edit while waiting for response
 * - Offline/online transitions
 *
 * ENVIRONMENT VARIABLES (with localStorage override):
 * - VITE_API_DELAY_ENABLED: 'true' | 'false' (default: false)
 * - VITE_API_DELAY_BEFORE_MS: number (default: 2500)
 * - VITE_API_DELAY_AFTER_MS: number (default: 2500)
 *
 * USAGE:
 * 1. Enable via env: VITE_API_DELAY_ENABLED=true
 * 2. Or via console: apiDelay.enable(beforeMs, afterMs)
 * 3. Or via localStorage: localStorage.setItem('API_DELAY_ENABLED', 'true')
 *
 * Example scenarios to test:
 * - User makes changes to flow A while batch update for flow B is in progress
 * - Two users edit the same pre-order simultaneously
 * - User refreshes data while sync is pending
 * - Offline → Online transition with queued changes
 * - User closes dialog/navigates while request is pending
 */

interface DelayConfig {
  enabled: boolean;
  beforeMs: number; // Delay before sending request
  afterMs: number; // Delay after receiving response
  pattern: RegExp | null;
  logRequests: boolean;
  showTimings: boolean; // Show detailed timing info
}

class APIDelayMiddleware {
  private config: DelayConfig = {
    enabled: false,
    beforeMs: 2500,
    afterMs: 2500,
    pattern: null,
    logRequests: false,
    showTimings: false,
  };

  private activeRequests = new Map<
    string,
    { url: string; method: string; startTime: number }
  >();

  constructor() {
    this.loadConfig();
  }

  private loadConfig() {
    if (typeof window === "undefined") {
      // Load from env vars on server side
      this.config = {
        enabled: import.meta.env.VITE_API_DELAY_ENABLED === "true",
        beforeMs: parseInt(
          import.meta.env.VITE_API_DELAY_BEFORE_MS || "2500",
          10,
        ),
        afterMs: parseInt(
          import.meta.env.VITE_API_DELAY_AFTER_MS || "2500",
          10,
        ),
        pattern: null,
        logRequests: false,
        showTimings: false,
      };
      return;
    }

    // Only allow in development/staging, never in production
    const isProd = import.meta.env.PROD;
    const isStaging = window.location.hostname.includes("staging");

    if (isProd && !isStaging) {
      this.config.enabled = false;
      return;
    }

    try {
      // Check localStorage first (overrides env vars)
      const hasLocalStorageConfig = localStorage.getItem("API_DELAY_ENABLED");

      const enabled =
        hasLocalStorageConfig !== null
          ? localStorage.getItem("API_DELAY_ENABLED") === "true"
          : import.meta.env.VITE_API_DELAY_ENABLED === "true";

      const beforeMs = parseInt(
        localStorage.getItem("API_DELAY_BEFORE_MS") ||
          import.meta.env.VITE_API_DELAY_BEFORE_MS ||
          "2500",
        10,
      );

      const afterMs = parseInt(
        localStorage.getItem("API_DELAY_AFTER_MS") ||
          import.meta.env.VITE_API_DELAY_AFTER_MS ||
          "2500",
        10,
      );

      const patternStr = localStorage.getItem("API_DELAY_PATTERN");
      const logRequests = localStorage.getItem("API_DELAY_LOG") === "true";
      const showTimings = localStorage.getItem("API_DELAY_TIMINGS") === "true";

      // Default pattern: only delay mobile recap endpoints
      // Pattern matches: /pre-orders/*, /pre-order-flows/*
      const defaultPattern = "pre-orders|pre-order-flows|unified-batch";

      this.config = {
        enabled,
        beforeMs: isNaN(beforeMs) ? 2500 : beforeMs,
        afterMs: isNaN(afterMs) ? 2500 : afterMs,
        pattern: patternStr
          ? new RegExp(patternStr, "i")
          : new RegExp(defaultPattern, "i"),
        logRequests,
        showTimings,
      };
    } catch (error) {
      console.warn("Failed to load API delay config:", error);
    }
  }

  /**
   * Delay BEFORE sending request (simulates slow network initiation)
   * Returns a unique request ID for tracking
   */
  async delayBefore(url: string, init?: RequestInit): Promise<string> {
    // Reload config on each request (allows runtime toggling)
    this.loadConfig();

    const requestId = Math.random().toString(36).substring(7);

    if (!this.config.enabled) return requestId;

    // Check if URL matches pattern (if pattern is set)
    if (this.config.pattern && !this.config.pattern.test(url)) {
      return requestId;
    }

    const method = init?.method || "GET";
    const beforeMs = this.config.beforeMs;

    // Track active request
    this.activeRequests.set(requestId, {
      url,
      method,
      startTime: Date.now(),
    });

    console.log(`⏱️ delay pre`);

    await new Promise((resolve) => setTimeout(resolve, beforeMs));

    return requestId;
  }

  /**
   * Delay AFTER receiving response (simulates slow server response)
   * Returns the response after delay
   */
  async delayAfter(requestId: string, response: Response): Promise<Response> {
    if (!this.config.enabled) {
      this.activeRequests.delete(requestId);
      return response;
    }

    const request = this.activeRequests.get(requestId);
    if (!request) return response;

    const afterMs = this.config.afterMs;
    const elapsedSoFar = Date.now() - request.startTime;

    console.log(`⏱️ delay post`);

    await new Promise((resolve) => setTimeout(resolve, afterMs));

    this.activeRequests.delete(requestId);
    return response;
  }

  /**
   * Get current configuration
   */
  getConfig(): DelayConfig {
    this.loadConfig();
    return { ...this.config };
  }

  /**
   * Enable delay middleware
   */
  enable(beforeMs: number = 2500, afterMs?: number) {
    if (typeof window === "undefined") return;
    localStorage.setItem("API_DELAY_ENABLED", "true");
    localStorage.setItem("API_DELAY_BEFORE_MS", beforeMs.toString());
    localStorage.setItem(
      "API_DELAY_AFTER_MS",
      (afterMs ?? beforeMs).toString(),
    );
    this.loadConfig();
    console.log(
      `✅ API delay middleware enabled (${beforeMs}ms before, ${afterMs ?? beforeMs}ms after)`,
    );
  }

  /**
   * Disable delay middleware
   */
  disable() {
    if (typeof window === "undefined") return;
    localStorage.setItem("API_DELAY_ENABLED", "false");
    this.loadConfig();
    console.log("❌ API delay middleware disabled");
  }

  /**
   * Set URL pattern to match (only delay matching requests)
   */
  setPattern(pattern: string) {
    if (typeof window === "undefined") return;
    localStorage.setItem("API_DELAY_PATTERN", pattern);
    this.loadConfig();
    console.log(`🎯 API delay pattern set: ${pattern}`);
  }

  /**
   * Clear pattern (delay all requests)
   */
  clearPattern() {
    if (typeof window === "undefined") return;
    localStorage.removeItem("API_DELAY_PATTERN");
    this.loadConfig();
    console.log("🔄 API delay pattern cleared (all requests)");
  }

  /**
   * Set individual delay values
   */
  setBefore(ms: number) {
    if (typeof window === "undefined") return;
    localStorage.setItem("API_DELAY_BEFORE_MS", ms.toString());
    this.loadConfig();
    console.log(`⏱️  Before delay set to ${ms}ms`);
  }

  setAfter(ms: number) {
    if (typeof window === "undefined") return;
    localStorage.setItem("API_DELAY_AFTER_MS", ms.toString());
    this.loadConfig();
    console.log(`⏱️  After delay set to ${ms}ms`);
  }

  /**
   * Get active request count
   */
  getActiveRequests(): number {
    return this.activeRequests.size;
  }

  /**
   * Get list of active requests
   */
  listActiveRequests(): Array<{
    url: string;
    method: string;
    elapsed: number;
  }> {
    const now = Date.now();
    return Array.from(this.activeRequests.values()).map((req) => ({
      url: req.url,
      method: req.method,
      elapsed: now - req.startTime,
    }));
  }

  /**
   * Print current status
   */
  status() {
    console.group("📊 API Delay Middleware Status");
    console.log("Enabled:", this.config.enabled);
    console.log("Before delay:", this.config.beforeMs + "ms");
    console.log("After delay:", this.config.afterMs + "ms");
    console.log("Pattern:", this.config.pattern?.source || "all requests");
    console.log("Active requests:", this.activeRequests.size);
    if (this.activeRequests.size > 0) {
      console.table(this.listActiveRequests());
    }
    console.groupEnd();
  }
}

// Singleton instance
export const apiDelayMiddleware = new APIDelayMiddleware();

// Expose to window for console access in development
if (typeof window !== "undefined" && import.meta.env.MODE !== "production") {
  (window as any).apiDelay = apiDelayMiddleware;

  // Log helpful message on page load if enabled
  if (apiDelayMiddleware.getConfig().enabled) {
    console.log("🐌 API Delay Middleware is ACTIVE - requests will be delayed");
    apiDelayMiddleware.status();
  }
}
